import {
    defaultDisplayConfigs,
    type CrmDisplayConfigContact,
    type CrmDisplayConfigField,
} from '@plato/data/model/crm-config';
import { DialogStateManager } from '@plato/ui-legacy/components/dialog/dialog-provider';
import { Button } from '@plato/ui-legacy/components/ui/button';
import { CopyButton } from '@plato/ui-legacy/components/ui/buttons/copy-button';
import { LinkText } from '@plato/ui-legacy/components/ui/link-text';
import type { ColumnDef } from '@plato/ui/components/data-table';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@plato/ui/containers/dropdown-menu';
import { MenuItemLabel, MenuItemLeading } from '@plato/ui/primitives/menu';
import { EllipsisVerticalIcon, Pencil } from 'lucide-react';
import { useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { GetCustomersContactsListItem } from '../../../server/types/infer-trpc-types.ts';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import { safeGetFieldByPath } from '../../crm/components/crm-context.tsx';
import { FormatValueWithDisplayConfig, toCopyText } from '../../crm/components/crm-field.tsx';
import { EditContactDialog } from '../../crm/components/edit-contact-dialog.tsx';
import { getCrmFieldLabel } from '../../crm/utils/crm-field-labels.ts';

export type UseCustomerContactsColumnsProps = {
    config?: CrmDisplayConfigContact;
};

export function useCustomerContactsColumns({
    config = defaultDisplayConfigs.contact,
}: UseCustomerContactsColumnsProps) {
    const { t } = useTranslation('customer-detail');

    const customerContactsColumns: ColumnDef<string, GetCustomersContactsListItem>[] = useMemo(
        () => [
            ...(config.listFields ?? config.fields ?? []).map(
                (field): ColumnDef<string, GetCustomersContactsListItem> => {
                    return {
                        id: field.path,
                        size: field.path === '/salutation' ? 70 : undefined,
                        header: getCrmFieldLabel(t, 'contact', field.path) ?? '',
                        cell: ({ row }) => <CustomerContactColumn field={field} row={row.original} />,
                    };
                },
            ),
            {
                id: 'actions',
                cell: ({ row }) => (
                    <div className="flex items-center justify-end">
                        <CopyButton
                            textToCopy={getContactCopyText(
                                config.listFields ?? config.fields ?? [],
                                row.original,
                            )}
                            label={t('action__copy_contact', { ns: 'company-contact-table' })}
                        />
                        <DropdownMenu>
                            <DropdownMenuTrigger
                                render={<Button variant="ghost" icon={EllipsisVerticalIcon} />}
                            />
                            <DropdownMenuContent align="end" side="bottom">
                                <DropdownMenuItem
                                    className="text-neutral-700"
                                    onClick={() => {
                                        void DialogStateManager.show(EditContactDialog, {
                                            customerId: row.original.customerId,
                                            contactId: row.original.contactId,
                                        });
                                    }}
                                >
                                    <MenuItemLeading>
                                        <Pencil />
                                    </MenuItemLeading>
                                    <MenuItemLabel>
                                        {t('action__edit', { ns: 'company-contact-table' })}
                                    </MenuItemLabel>
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                ),
                meta: {
                    align: 'right',
                    pinned: 'right',
                },
                size: 1,
            },
        ],
        [t, config],
    );

    return customerContactsColumns;
}

/**
 * A cell-level copy icon would put one glyph in every cell of every row, so the
 * table copies the whole contact instead: the visible columns, one per line.
 */
function getContactCopyText(fields: CrmDisplayConfigField[], row: GetCustomersContactsListItem): string {
    return fields
        .map((field) =>
            field.path === '/name'
                ? [row.firstName, row.lastName].filter(Boolean).join(' ')
                : toCopyText(safeGetFieldByPath(row, field.path)),
        )
        .filter(Boolean)
        .join('\n');
}

type CustomerContactColumnProps = {
    field: CrmDisplayConfigField;
    row: GetCustomersContactsListItem;
};

function CustomerContactColumn({ field, row }: CustomerContactColumnProps): ReactNode {
    const sendEvent = useAnalytics();

    /* eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion 
    -- just to guard spelling mistakes in this function; if it is invalid, then it won't render anything */
    const path = field.path as `/${keyof GetCustomersContactsListItem}`;

    if (path === '/email') {
        return row.email ? (
            <a
                className="text-blue-700 hover:text-blue-700"
                href={`mailto:${row.email}`}
                rel="noopener noreferrer"
                target="_blank"
                onClick={() => {
                    sendEvent('customer_contact_email_clicked', {
                        customer_id: row.customerId,
                        contact_id: row.contactId,
                        contact_type: 'email',
                    });
                }}
            >
                {row.email}
            </a>
        ) : (
            '-'
        );
    }
    if (path === '/name') {
        return [row.firstName, row.lastName].filter(Boolean).join(' ');
    }
    if (path === '/mobile') {
        return row.mobile ? (
            <LinkText
                href={`tel:${row.mobile}`}
                onClick={() => {
                    sendEvent('customer_contact_phone_clicked', {
                        customer_id: row.customerId,
                        contact_id: row.contactId,
                        contact_type: 'mobile',
                    });
                }}
            >
                {row.mobile}
            </LinkText>
        ) : (
            '-'
        );
    }
    if (path === '/phone') {
        return row.phone ? (
            <LinkText
                href={`tel:${row.phone}`}
                onClick={() => {
                    sendEvent('customer_contact_phone_clicked', {
                        customer_id: row.customerId,
                        contact_id: row.contactId,
                        contact_type: 'phone',
                    });
                }}
            >
                {row.phone}
            </LinkText>
        ) : (
            '-'
        );
    }
    return <FormatValueWithDisplayConfig config={field} value={safeGetFieldByPath(row, field.path)} />;
}
